@file:OptIn(ExperimentalBlockRichTextApi::class)

package com.taocent.simple.compose.component.blockrichtext.internal.block.renderer

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.width
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.zIndex
import com.taocent.simple.compose.component.blockrichtext.ExperimentalBlockRichTextApi
import com.taocent.simple.compose.component.blockrichtext.*
import com.taocent.simple.compose.component.richtext.core.RichTextState
import com.taocent.simple.compose.component.richtext.core.TableConfig
import kotlin.math.roundToInt

/**
 * 独立的单元格 Composable,隔离单元格的重组范围。
 * 抽离自 `TableRenderer.kt`,通过参数接收所有需要的输入。
 */
@Composable
internal fun TableCell(
    cell: com.taocent.simple.compose.component.blockrichtext.TableBlock.TableCell,
    rowId: String,
    rowIndex: Int,
    cellIndex: Int,
    columnWidthDp: Dp,
    blockId: String,
    blockRows: Int,
    blockCols: Int,
    blockState: com.taocent.simple.compose.component.blockrichtext.BlockState,
    tableConfig: TableConfig,
    primaryColor: Color,
    isTableSelected: Boolean,
    isNavSelectedLeft: Boolean,
    isNavSelectedRight: Boolean,
    isRowAnchorLeft: Boolean,
    isRowAnchorRight: Boolean,
    isSelectedRow: Boolean,
    isSelectedCol: Boolean,
    drawRightBorder: Boolean,
    drawBottomBorder: Boolean,
    cellColOffset: Float,
    isCellLifted: Boolean,
    cellStates: MutableMap<String, RichTextState>,
    cellSyncVersions: MutableMap<String, Int>,
    cellFocusRequesters: MutableMap<Pair<String, String>, FocusRequester>,
    onFocus: (rowIndex: Int, cellIndex: Int, rowId: String, cellId: String, cellState: RichTextState, focusRequester: FocusRequester) -> Unit,
    onClearSelection: () -> Unit,
    onTextLayout: (rowId: String, cellId: String, layout: TextLayoutResult) -> Unit = { _, _, _ -> },
) {
    val isHeader = rowIndex == 0
    val isEvenRow = rowIndex % 2 == 0
    val backgroundColor = when {
        isHeader -> tableConfig.headerColor
        isEvenRow -> tableConfig.headerColor
        else -> tableConfig.cellColor
    }
    val isFirstRow = rowIndex == 0
    val isLastRow = rowIndex == blockRows - 1
    val isFirstCol = cellIndex == 0
    val isLastCol = cellIndex == blockCols - 1
    val isOuterCell = isTableSelected && (isFirstRow || isLastRow || isFirstCol || isLastCol)

    val key = "${blockId}_${rowId}_${cell.cellId}"
    val content = cell.content
    val cellState = cellStates.getOrPut(key) {
        val newState = RichTextState()
        if (content.isNotEmpty()) {
            newState.restoreTextFieldValue(
                TextFieldValue(
                    annotatedString = content,
                    selection = TextRange(content.length)
                )
            )
        }
        newState
    }
    if (cellState.textFieldValue.annotatedString != content &&
        cellSyncVersions[key] != blockState.contentSyncVersion
    ) {
        cellState.restoreTextFieldValue(
            TextFieldValue(
                annotatedString = content,
                selection = TextRange(content.length)
            )
        )
    }
    cellSyncVersions[key] = blockState.contentSyncVersion

    val cellFocusRequester = remember(rowId, cell.cellId) { FocusRequester() }
    DisposableEffect(rowId, cell.cellId) {
        cellFocusRequesters[Pair(rowId, cell.cellId)] = cellFocusRequester
        onDispose {
            cellFocusRequesters.remove(Pair(rowId, cell.cellId))
        }
    }

    val cellModifier = Modifier
        .width(columnWidthDp)
        .fillMaxHeight()
        .offset { IntOffset(cellColOffset.roundToInt(), 0) }
        .background(backgroundColor)
        .drawWithContent {
            drawContent()
            val primaryStroke = tableConfig.selectedHighlightBorderWidth.toPx()
            val normalStroke = tableConfig.cellBorderWidth.toPx()

            drawLine(
                tableConfig.borderColor,
                Offset(normalStroke / 2, 0f),
                Offset(normalStroke / 2, size.height),
                normalStroke
            )
            drawLine(
                tableConfig.borderColor,
                Offset(0f, normalStroke / 2),
                Offset(size.width, normalStroke / 2),
                normalStroke
            )
            if (drawRightBorder) {
                drawLine(
                    tableConfig.borderColor,
                    Offset(size.width - normalStroke / 2, 0f),
                    Offset(size.width - normalStroke / 2, size.height),
                    normalStroke
                )
            }
            if (drawBottomBorder) {
                drawLine(
                    tableConfig.borderColor,
                    Offset(0f, size.height - normalStroke / 2),
                    Offset(size.width, size.height - normalStroke / 2),
                    normalStroke
                )
            }

            if (isTableSelected) {
                if (isFirstRow) {
                    drawLine(
                        primaryColor,
                        Offset(0f, primaryStroke / 2),
                        Offset(size.width, primaryStroke / 2),
                        primaryStroke
                    )
                }
                if (isLastRow) {
                    drawLine(
                        primaryColor,
                        Offset(0f, size.height - primaryStroke / 2),
                        Offset(size.width, size.height - primaryStroke / 2),
                        primaryStroke
                    )
                }
                if (isFirstCol) {
                    drawLine(
                        primaryColor,
                        Offset(primaryStroke / 2, 0f),
                        Offset(primaryStroke / 2, size.height),
                        primaryStroke
                    )
                }
                if (isLastCol) {
                    drawLine(
                        primaryColor,
                        Offset(size.width - primaryStroke / 2, 0f),
                        Offset(size.width - primaryStroke / 2, size.height),
                        primaryStroke
                    )
                }
            }

            if (isNavSelectedLeft && cellIndex == 0) {
                drawLine(
                    primaryColor,
                    Offset(primaryStroke / 2, 0f),
                    Offset(primaryStroke / 2, size.height),
                    primaryStroke
                )
            }
            // 锚点 After 态:在最末列 cell 的右边缘画竖线,与左高亮对称。
            // 用 cellIndex == blockCols - 1 而不是 isLastCol(同值,这里复用)。
            if (isNavSelectedRight && cellIndex == blockCols - 1) {
                drawLine(
                    primaryColor,
                    Offset(size.width - primaryStroke / 2, 0f),
                    Offset(size.width - primaryStroke / 2, size.height),
                    primaryStroke
                )
            }

            // 行锚点左高亮:仅在该行首列 cell 的左边缘画竖线
            if (isRowAnchorLeft && isFirstCol) {
                drawLine(primaryColor, Offset(primaryStroke / 2, 0f), Offset(primaryStroke / 2, size.height), primaryStroke)
            }
            // 行锚点右高亮:仅在该行末列 cell 的右边缘画竖线
            if (isRowAnchorRight && isLastCol) {
                drawLine(primaryColor, Offset(size.width - primaryStroke / 2, 0f), Offset(size.width - primaryStroke / 2, size.height), primaryStroke)
            }

            // 行删除保护高亮:当 navRowDeleteGuardRowId 命中当前行时,整行画外框高亮。
            // 始终画上+下水平线和左+右垂直线,不受 isFirstRow/isLastRow 限制,
            // 因为目标行可能是中间行,其上下边界都需要高亮。
            val isRowDeleteGuard = blockState.navRowDeleteGuardRowId == rowId
            if (isRowDeleteGuard) {
                drawLine(primaryColor, Offset(0f, primaryStroke / 2), Offset(size.width, primaryStroke / 2), primaryStroke)
                drawLine(primaryColor, Offset(0f, size.height - primaryStroke / 2), Offset(size.width, size.height - primaryStroke / 2), primaryStroke)
                if (isFirstCol) {
                    drawLine(primaryColor, Offset(primaryStroke / 2, 0f), Offset(primaryStroke / 2, size.height), primaryStroke)
                }
                if (isLastCol) {
                    drawLine(primaryColor, Offset(size.width - primaryStroke / 2, 0f), Offset(size.width - primaryStroke / 2, size.height), primaryStroke)
                }
            }

            if (isSelectedRow) {
                drawLine(
                    primaryColor,
                    Offset(0f, primaryStroke / 2),
                    Offset(size.width, primaryStroke / 2),
                    primaryStroke
                )
                drawLine(
                    primaryColor,
                    Offset(0f, size.height - primaryStroke / 2),
                    Offset(size.width, size.height - primaryStroke / 2),
                    primaryStroke
                )
                if (isFirstCol) {
                    drawLine(
                        primaryColor,
                        Offset(primaryStroke / 2, 0f),
                        Offset(primaryStroke / 2, size.height),
                        primaryStroke
                    )
                }
                if (isLastCol) {
                    drawLine(
                        primaryColor,
                        Offset(size.width - primaryStroke / 2, 0f),
                        Offset(size.width - primaryStroke / 2, size.height),
                        primaryStroke
                    )
                }
            }

            if (isSelectedCol) {
                drawLine(
                    primaryColor,
                    Offset(primaryStroke / 2, 0f),
                    Offset(primaryStroke / 2, size.height),
                    primaryStroke
                )
                drawLine(
                    primaryColor,
                    Offset(size.width - primaryStroke / 2, 0f),
                    Offset(size.width - primaryStroke / 2, size.height),
                    primaryStroke
                )
                if (isFirstRow) {
                    drawLine(
                        primaryColor,
                        Offset(0f, primaryStroke / 2),
                        Offset(size.width, primaryStroke / 2),
                        primaryStroke
                    )
                }
                if (isLastRow) {
                    drawLine(
                        primaryColor,
                        Offset(0f, size.height - primaryStroke / 2),
                        Offset(size.width, size.height - primaryStroke / 2),
                        primaryStroke
                    )
                }
            }
        }

    Box(modifier = cellModifier
        .zIndex(if (isCellLifted) 1f else 0f)
        .clickable(
            interactionSource = remember { MutableInteractionSource() },
            indication = null
        ) { cellFocusRequester.requestFocus() }
    ) {
        TableCellEditor(
            state = cellState,
            isHeader = isHeader,
            focusRequester = cellFocusRequester,
            onFocus = { focusRequester ->
                onFocus(rowIndex, cellIndex, rowId, cell.cellId, cellState, focusRequester)
            },
            onClearSelection = onClearSelection,
            onContentChange = {
                blockState.updateTableCellContent(
                    tableId = blockId,
                    rowId = rowId,
                    cellId = cell.cellId,
                    content = cellState.textFieldValue.annotatedString
                )
            },
            onUndo = { blockState.undo() },
            onRedo = { blockState.redo() },
            onTextLayout = { layout -> onTextLayout(rowId, cell.cellId, layout) },
            modifier = Modifier.fillMaxWidth()
        )
    }
}
